import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { KommaLogo } from "@/components/komma/KommaLogo";
import NotificationBell from "@/components/NotificationBell";
import {
    LayoutDashboard, Users, Wallet, FileCheck2, Bell, Phone, LogOut,
    Building2, BarChart3, Briefcase, MapPin, Network, FileBarChart2, Shield,
} from "lucide-react";

const NAV = {
    ceo: [
        { to: "/", label: "Overview", icon: LayoutDashboard },
        { to: "/loans", label: "Loans", icon: Wallet },
        { to: "/customers", label: "Customers", icon: Users },
        { to: "/documents", label: "AI OCR", icon: FileCheck2 },
        { to: "/visits", label: "Visits", icon: MapPin },
        { to: "/branches", label: "Branches", icon: Network },
        { to: "/reports", label: "Reports", icon: FileBarChart2 },
        { to: "/analytics", label: "Analytics", icon: BarChart3 },
        { to: "/employees", label: "Employees", icon: Briefcase },
        { to: "/audit", label: "Audit Log", icon: Shield },
        { to: "/notifications", label: "Notifications", icon: Bell },
    ],
    office_admin: [
        { to: "/", label: "Operations", icon: LayoutDashboard },
        { to: "/loans", label: "Loans", icon: Wallet },
        { to: "/customers", label: "Customers", icon: Users },
        { to: "/documents", label: "AI OCR", icon: FileCheck2 },
        { to: "/visits", label: "Visits", icon: MapPin },
        { to: "/reports", label: "Reports", icon: FileBarChart2 },
        { to: "/employees", label: "Employees", icon: Briefcase },
        { to: "/notifications", label: "Notifications", icon: Bell },
    ],
    branch_manager: [
        { to: "/", label: "Branch Desk", icon: LayoutDashboard },
        { to: "/loans", label: "Loans", icon: Wallet },
        { to: "/customers", label: "Customers", icon: Users },
        { to: "/documents", label: "Documents", icon: FileCheck2 },
        { to: "/visits", label: "Visits", icon: MapPin },
        { to: "/notifications", label: "Notifications", icon: Bell },
    ],
    marketing_executive: [
        { to: "/", label: "Pipeline", icon: LayoutDashboard },
        { to: "/customers", label: "Leads", icon: Users },
        { to: "/loans", label: "Applications", icon: Wallet },
        { to: "/notifications", label: "Notifications", icon: Bell },
    ],
    agent: [
        { to: "/", label: "Field", icon: LayoutDashboard },
        { to: "/loans", label: "My Loans", icon: Wallet },
        { to: "/customers", label: "Customers", icon: Users },
        { to: "/visits", label: "My Visits", icon: MapPin },
        { to: "/documents", label: "Verify Docs", icon: FileCheck2 },
        { to: "/notifications", label: "Notifications", icon: Bell },
    ],
    telecaller: [
        { to: "/", label: "Call Queue", icon: LayoutDashboard },
        { to: "/customers", label: "Leads", icon: Users },
        { to: "/calls", label: "Call Logs", icon: Phone },
        { to: "/notifications", label: "Notifications", icon: Bell },
    ],
    customer: [
        { to: "/", label: "My Portfolio", icon: LayoutDashboard },
        { to: "/loans", label: "My Loans", icon: Wallet },
        { to: "/documents", label: "Documents", icon: FileCheck2 },
        { to: "/notifications", label: "Notifications", icon: Bell },
    ],
};

const ROLE_LABEL = {
    ceo: "CEO",
    office_admin: "Office Admin",
    branch_manager: "Branch Manager",
    marketing_executive: "Marketing Exec",
    agent: "Agent",
    telecaller: "Telecaller",
    customer: "Customer",
};

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const items = NAV[user?.role] || NAV.customer;

    return (
        <div className="min-h-screen flex bg-komma-cream">
            <aside className="w-64 shrink-0 bg-komma-navy text-white flex flex-col" data-testid="app-sidebar">
                <div className="px-6 py-6 flex flex-col items-center border-b border-white/10">
                    <div className="bg-white/95 rounded-xl p-3">
                        <KommaLogo size={72} />
                    </div>
                    <div className="komma-gold-hairline w-3/4 mt-4" />
                    <p className="mt-2 text-[10px] uppercase tracking-[0.28em] text-komma-gold">
                        Trust · Growth · Prosperity
                    </p>
                </div>

                <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
                    {items.map(({ to, label, icon: Icon }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={to === "/"}
                            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                                    isActive ? "sidebar-active text-white" : "text-white/70 hover:text-white hover:bg-white/5"
                                }`
                            }
                        >
                            <Icon size={18} />
                            {label}
                        </NavLink>
                    ))}
                </nav>

                <div className="p-4 border-t border-white/10">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="h-9 w-9 rounded-full bg-komma-gold text-komma-navy flex items-center justify-center font-serif text-lg font-bold">
                            {user?.name?.[0] || "K"}
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-semibold truncate">{user?.name}</p>
                            <p className="text-[10px] uppercase tracking-wider text-komma-gold">{ROLE_LABEL[user?.role]}</p>
                        </div>
                    </div>
                    <button
                        onClick={async () => { await logout(); nav("/login"); }}
                        data-testid="logout-btn"
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-white/80 hover:bg-white/10 transition-colors"
                    >
                        <LogOut size={16} /> Sign out
                    </button>
                </div>
            </aside>

            <main className="flex-1 min-w-0 flex flex-col">
                <header className="h-16 bg-white border-b border-gray-200 flex items-center px-8 gap-4" data-testid="app-header">
                    <Building2 size={18} className="text-komma-navy" />
                    <h1 className="font-serif text-lg text-komma-navy" data-testid="header-title">Komma Associates</h1>
                    <div className="komma-gold-hairline w-16" />
                    <span className="text-xs uppercase tracking-[0.24em] text-gray-500">{ROLE_LABEL[user?.role]} Console</span>
                    <div className="ml-auto flex items-center gap-4">
                        <NotificationBell />
                        <span className="text-sm text-gray-600">Hello, <span className="font-semibold text-komma-navy">{user?.name?.split(" ")[0]}</span></span>
                    </div>
                </header>
                <div className="flex-1 p-8 overflow-y-auto animate-fade-up" data-testid="page-content">
                    {children}
                </div>
            </main>
        </div>
    );
}
